import React, { useState, useCallback } from "react";
import { useLanguage } from "@/context/LanguageContext";
import { motion, AnimatePresence } from "framer-motion";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Send, CheckCircle, AlertCircle, Phone, Clock, MessageCircle, MapPin, ChevronDown } from "lucide-react";
import { LocationPickerMap } from "@/components/MapComponents";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PHONE = "(936) 249-8196";
const PHONE_LINK = "tel:+19362498196";
const WA_NUMBER = "19362498196";

function buildWhatsAppUrl(form, lang) {
  const typeName = form.problem_type || "";
  const lines = [
    lang === "es" ? "Hola, necesito servicio de cerrajería." : "Hi, I need locksmith service.",
    `${lang === "es" ? "Nombre" : "Name"}: ${form.name}`,
    `${lang === "es" ? "Teléfono" : "Phone"}: ${form.phone}`,
    `${lang === "es" ? "Ubicación" : "Location"}: ${form.location}`,
    `${lang === "es" ? "Tipo" : "Type"}: ${typeName}`,
  ];
  if (form.vehicle_model) lines.push(`${lang === "es" ? "Vehículo" : "Vehicle"}: ${form.vehicle_model}`);
  if (form.description) lines.push(`${lang === "es" ? "Detalles" : "Details"}: ${form.description}`);
  return `https://wa.me/${WA_NUMBER}?text=${encodeURIComponent(lines.join("\n"))}`;
}

export default function ServiceRequestForm() {
  const { t, lang } = useLanguage();
  const [form, setForm] = useState({ name: "", phone: "", location: "", problem_type: "", vehicle_model: "", description: "" });
  const [status, setStatus] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [submittedData, setSubmittedData] = useState(null);
  const [mapCoords, setMapCoords] = useState({ lat: null, lng: null });
  const [showMap, setShowMap] = useState(false);

  const isAutomotive = form.problem_type === "automotive";

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    if (field === "problem_type" && value !== "automotive") {
      setForm((prev) => ({ ...prev, vehicle_model: "", [field]: value }));
    }
  };

  const handleLocationSelect = useCallback(async (lat, lng) => {
    setMapCoords({ lat, lng });
    try {
      const res = await fetch(`https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&zoom=16`);
      const data = await res.json();
      if (data.display_name) {
        const short = [data.address?.road, data.address?.neighbourhood || data.address?.suburb, data.address?.city || data.address?.town, data.address?.state].filter(Boolean).join(", ");
        handleChange("location", short || data.display_name);
      }
    } catch {
      handleChange("location", `${lat.toFixed(4)}, ${lng.toFixed(4)}`);
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus("submitting");
    try {
      const payload = { ...form };
      if (!isAutomotive) delete payload.vehicle_model;
      if (!payload.description) delete payload.description;
      await axios.post(`${API}/service-requests`, payload);
      setStatus("success");
      setSubmittedData({ ...form });
      setSubmitted(true);
    } catch {
      setStatus("error");
      setTimeout(() => setStatus(null), 5000);
    }
  };

  const typeKeys = ["emergency", "residential", "commercial", "automotive", "safes", "security"];

  const inputClass = "bg-black/50 border-white/10 text-white rounded-xl px-4 py-3 h-12 focus-visible:ring-2 focus-visible:ring-[#D4AF37]/50 focus-visible:border-[#D4AF37] placeholder:text-[#8A8881]";
  const labelClass = "block text-sm text-white/70 mb-2";

  return (
    <section id="contact" data-testid="contact-section" className="py-24 md:py-32 bg-[#0B0A08] noise-overlay">
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16 relative z-10">
        <div className="grid lg:grid-cols-5 gap-12 lg:gap-16">
          {/* Left info */}
          <motion.div initial={{ opacity: 0, x: -20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.5 }} className="lg:col-span-2">
            <span className="text-xs md:text-sm font-bold uppercase tracking-[0.2em] text-[#D4AF37]">{t.form.badge}</span>
            <h2 className="font-heading text-3xl md:text-4xl font-semibold tracking-tight text-white mt-3 mb-4">{t.form.title}</h2>
            <p className="text-base text-[#8A8881] leading-relaxed mb-10">{t.form.subtitle}</p>

            <div className="bg-[#141311] border border-white/5 rounded-2xl p-6 flex items-center gap-4 glow-gold">
              <div className="w-14 h-14 bg-gradient-to-r from-[#BF953F] via-[#F9E58A] to-[#B38728] rounded-xl flex items-center justify-center flex-shrink-0">
                <Clock className="w-6 h-6 text-black" />
              </div>
              <div>
                <p className="font-semibold text-white text-sm">{t.form.responseNote}</p>
                <p className="text-xs text-[#8A8881] mt-0.5">24/7/365</p>
              </div>
            </div>
          </motion.div>

          {/* Form / Success */}
          <motion.div initial={{ opacity: 0, x: 20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.5 }} className="lg:col-span-3">
            <AnimatePresence mode="wait">
              {!submitted ? (
                <motion.form
                  key="form"
                  data-testid="service-request-form"
                  onSubmit={handleSubmit}
                  className="bg-[#141311]/80 backdrop-blur-xl border border-white/10 rounded-2xl p-6 lg:p-8 space-y-5 shadow-[0_8px_32px_rgba(0,0,0,0.4)]"
                  exit={{ opacity: 0, y: -20 }}
                >
                  <div className="grid sm:grid-cols-2 gap-5">
                    <div>
                      <label className={labelClass}>{t.form.name}</label>
                      <Input data-testid="input-name" required value={form.name} onChange={(e) => handleChange("name", e.target.value)} placeholder={t.form.namePlaceholder} className={inputClass} />
                    </div>
                    <div>
                      <label className={labelClass}>{t.form.phone}</label>
                      <Input data-testid="input-phone" required type="tel" value={form.phone} onChange={(e) => handleChange("phone", e.target.value)} placeholder={t.form.phonePlaceholder} className={inputClass} />
                    </div>
                  </div>

                  <div>
                    <label className={labelClass}>{t.form.location}</label>
                    <div className="flex gap-2">
                      <Input data-testid="input-location" required value={form.location} onChange={(e) => handleChange("location", e.target.value)} placeholder={t.form.locationPlaceholder} className={`${inputClass} flex-1`} />
                      <button type="button" data-testid="toggle-map-btn" onClick={() => setShowMap(!showMap)} className={`flex items-center gap-1.5 px-4 h-12 text-xs font-semibold rounded-xl border transition-all duration-300 ${showMap ? "bg-[#D4AF37] text-black border-[#D4AF37]" : "bg-transparent text-[#D4AF37] border-[#D4AF37]/30 hover:bg-[#D4AF37]/10"}`}>
                        <MapPin className="w-3.5 h-3.5" />
                        {lang === "es" ? "Mapa" : "Map"}
                      </button>
                    </div>
                    {showMap && (
                      <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 250 }} className="mt-3 rounded-xl overflow-hidden border border-white/10" data-testid="location-picker-map">
                        <div style={{ height: 250 }}>
                          <LocationPickerMap selectedLat={mapCoords.lat} selectedLng={mapCoords.lng} onLocationSelect={handleLocationSelect} />
                        </div>
                        <p className="text-[10px] text-[#8A8881] bg-[#141311] px-3 py-1.5 border-t border-white/5">
                          {lang === "es" ? "Toque el mapa para seleccionar su ubicación" : "Tap the map to select your location"}
                        </p>
                      </motion.div>
                    )}
                  </div>

                  <div>
                    <label className={labelClass}>{t.form.problemType}</label>
                    <div className="relative">
                      <select
                        data-testid="select-problem-type"
                        value={form.problem_type}
                        onChange={(e) => handleChange("problem_type", e.target.value)}
                        required
                        className="w-full appearance-none bg-black/50 border border-white/10 text-white rounded-xl px-4 py-3 h-12 focus:ring-2 focus:ring-[#D4AF37]/50 focus:border-[#D4AF37] outline-none transition-all cursor-pointer"
                      >
                        <option value="" disabled className="bg-[#1C1B18] text-[#8A8881]">{t.form.problemPlaceholder}</option>
                        {typeKeys.map((key) => (
                          <option key={key} value={key} data-testid={`option-${key}`} className="bg-[#1C1B18] text-white py-2">
                            {t.form.types[key]}
                          </option>
                        ))}
                      </select>
                      <ChevronDown className="absolute right-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[#8A8881] pointer-events-none" />
                    </div>
                  </div>

                  {isAutomotive && (
                    <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }}>
                      <label className={labelClass}>{t.form.vehicleModel}</label>
                      <Input data-testid="input-vehicle-model" value={form.vehicle_model} onChange={(e) => handleChange("vehicle_model", e.target.value)} placeholder={t.form.vehiclePlaceholder} className={inputClass} />
                    </motion.div>
                  )}

                  <div>
                    <label className={labelClass}>{t.form.description}</label>
                    <Textarea data-testid="input-description" value={form.description} onChange={(e) => handleChange("description", e.target.value)} placeholder={t.form.descriptionPlaceholder} rows={4} className="bg-black/50 border-white/10 text-white rounded-xl px-4 py-3 focus-visible:ring-2 focus-visible:ring-[#D4AF37]/50 focus-visible:border-[#D4AF37] placeholder:text-[#8A8881]" />
                  </div>

                  <button
                    data-testid="submit-service-request"
                    type="submit"
                    disabled={status === "submitting"}
                    className="w-full flex items-center justify-center gap-2 bg-gradient-to-r from-[#BF953F] via-[#F9E58A] to-[#B38728] text-black px-8 py-4 text-sm font-bold rounded-full hover:shadow-[0_0_30px_rgba(212,175,55,0.4)] transition-all duration-300 transform hover:-translate-y-0.5 disabled:opacity-60"
                  >
                    {status === "submitting" ? (
                      <><div className="w-4 h-4 border-2 border-black/30 border-t-black rounded-full animate-spin" />{t.form.submitting}</>
                    ) : (
                      <><Send className="w-4 h-4" />{t.form.submit}</>
                    )}
                  </button>

                  {status === "error" && (
                    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} data-testid="form-error-msg" className="flex items-center gap-2 text-red-400 bg-red-900/20 border border-red-500/20 rounded-xl p-3 text-sm">
                      <AlertCircle className="w-4 h-4 flex-shrink-0" />{t.form.error}
                    </motion.div>
                  )}
                </motion.form>
              ) : (
                <motion.div
                  key="success"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  data-testid="form-success-panel"
                  className="bg-[#141311]/80 backdrop-blur-xl border border-white/10 rounded-2xl p-6 lg:p-8 shadow-[0_8px_32px_rgba(0,0,0,0.4)]"
                >
                  <div className="flex items-center gap-3 mb-8">
                    <div className="w-12 h-12 bg-green-900/30 border border-green-500/20 rounded-full flex items-center justify-center flex-shrink-0">
                      <CheckCircle className="w-6 h-6 text-green-400" />
                    </div>
                    <p className="text-base font-medium text-white">{t.form.success}</p>
                  </div>

                  <div className="space-y-4">
                    <a
                      data-testid="call-now-btn"
                      href={PHONE_LINK}
                      className="flex items-center justify-center gap-3 w-full bg-gradient-to-r from-red-800 to-red-700 text-white px-8 py-5 rounded-2xl hover:shadow-[0_0_20px_rgba(200,50,50,0.3)] transition-all duration-300"
                    >
                      <Phone className="w-5 h-5" />
                      <div className="text-center">
                        <span className="block text-sm font-semibold">{t.form.callNow}</span>
                        <span className="block text-xl font-heading font-bold">{PHONE}</span>
                      </div>
                    </a>

                    <a
                      data-testid="whatsapp-btn"
                      href={buildWhatsAppUrl(submittedData || form, lang)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center justify-center gap-3 w-full bg-[#25D366] text-white px-8 py-5 rounded-2xl hover:shadow-[0_0_20px_rgba(37,211,102,0.3)] transition-all duration-300"
                    >
                      <MessageCircle className="w-5 h-5" />
                      <div className="text-center">
                        <span className="block text-sm font-semibold">{t.form.whatsapp}</span>
                        <span className="block text-xl font-heading font-bold">{PHONE}</span>
                      </div>
                    </a>
                  </div>

                  <p className="text-xs text-[#8A8881] text-center mt-6">{t.form.responseNote}</p>

                  <button
                    data-testid="new-request-btn"
                    onClick={() => { setSubmitted(false); setStatus(null); setForm({ name: "", phone: "", location: "", problem_type: "", vehicle_model: "", description: "" }); }}
                    className="w-full mt-4 text-sm text-[#D4AF37] hover:text-[#F0C84A] font-medium transition-colors"
                  >
                    {t.form.submit} &rarr;
                  </button>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
